import { Box, IconButton, Paper, Stack, Typography } from "@mui/material";
import { CategoryOutlined, LightbulbOutlined, ElectricBoltOutlined, BatteryFullOutlined, SettingsOutlined, TireRepairOutlined } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { GridColDef } from "@mui/x-data-grid";

import { AppDataTable, AppStatusSwitch } from "../../../../components/common";
import { Category, RecordStatus } from "../types";

interface Props {
  rows: Category[];
  loading: boolean;
  rowCount?: number;
  page?: number;
  pageSize?: number;
  onEdit: (category: Category) => void;
  onDelete: (category: Category) => void;
  onStatusChange: (category: Category, status: RecordStatus) => void;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}

const CategoryTable = ({
  rows,
  loading,
  rowCount = rows.length,
  page = 0,
  pageSize = 10,
  onEdit,
  onDelete,
  onStatusChange,
  onPageChange,
  onPageSizeChange,
}: Props) => {
  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Category",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => {
        const name = String(params.row.name).toLowerCase();
        const style = name.includes("light") ? { icon: <LightbulbOutlined />, color: "#0284c7" }
          : name.includes("elect") ? { icon: <ElectricBoltOutlined />, color: "#8b5cf6" }
          : name.includes("batter") ? { icon: <BatteryFullOutlined />, color: "#ef4444" }
          : name.includes("tyre") ? { icon: <TireRepairOutlined />, color: "#2563eb" }
          : name.includes("motor") || name.includes("brake") ? { icon: <SettingsOutlined />, color: "#0d9488" }
          : { icon: <CategoryOutlined />, color: "#6366f1" };
        return <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ width: 34, height: 30, borderRadius: 1.5, display: "grid", placeItems: "center", color: style.color, bgcolor: alpha(style.color, .10), "& svg": { fontSize: 20 } }}>{style.icon}</Box>
          <Typography variant="body2" fontWeight={600}>{params.row.name}</Typography>
        </Stack>;
      },
    },
    {
      field: "code",
      headerName: "Code",
      width: 140,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1.5,
      minWidth: 220,
      valueGetter: (params) => params.row.description || "-",
    },
    {
      field: "displayOrder",
      headerName: "Order",
      width: 90,
    },
    {
      field: "status",
      headerName: "Status",
      width: 180,
      renderCell: (params) => (
        <AppStatusSwitch
          checked={params.row.status === "ACTIVE"}
          onChange={(checked) =>
            onStatusChange(params.row, checked ? "ACTIVE" : "INACTIVE")
          }
        />
      ),
    },
    {
      field: "createdAt",
      headerName: "Created",
      width: 140,
      valueGetter: (params) =>
        params.row.createdAt
          ? new Date(params.row.createdAt).toLocaleDateString()
          : "-",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <IconButton
            color="primary"
            aria-label={`Edit ${params.row.name}`}
            size="small"
            onClick={() => onEdit(params.row)}
          >
            <EditIcon fontSize="small" />
          </IconButton>

          <IconButton
            color="error"
            aria-label={`Delete ${params.row.name}`}
            size="small"
            onClick={() => onDelete(params.row)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <AppDataTable
        rows={rows}
        columns={columns}
        loading={loading}
        rowCount={rowCount}
        page={page}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </Paper>
  );
};

export default CategoryTable;
